import { FC, useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import { IAgoraRTCRemoteUser } from "agora-rtc-sdk-ng";
import AgoraRTC from "agora-rtc-sdk-ng";
import { useLiveRoom } from "../../hooks/useLiveRoom";

interface Participant {
  userId: string;
  name: string;
  role: string;
  peerId?: string;

  id: string;
  handRaised: boolean;
  raisedAt?: Date;
}

const LiveClassroom: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const remoteUserRef = useRef<IAgoraRTCRemoteUser | null>(null);

  // State
  const [isMicOn, setIsMicOn] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [participants, setParticipants] = useState<Participant[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [isConnecting, setIsConnecting] = useState(true);

  // Whiteboard state - simplified since we use screen sharing
  const [isWhiteboardActive, setIsWhiteboardActive] = useState(false);
  // Network quality monitoring removed
  const [audioVolume, setAudioVolume] = useState(100);
  const [showVolumeControl, setShowVolumeControl] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);

  // Whiteboard drawing functions removed - using screen sharing instead

  // Shared Agora RTC + Socket.IO lifecycle (join, chat, connection, teardown).
  const {
    agoraServiceRef,
    streamServiceRef,
    isSocketConnected,
    messages,
    sendMessage,
  } = useLiveRoom({
    roomId: id,
    role: "user",
    onConnected: () => setIsConnecting(false),
  });

  const sessionTitle = location.state?.title;

  // Network quality components removed

  // Add browser compatibility check
  const checkBrowserSupport = () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.warn("MediaDevices API not supported");
      return false;
    }

    if (!window.RTCPeerConnection) {
      console.warn("WebRTC not supported");
      return false;
    }

    return true;
  };

  // Surface the transient reconnect notifications the student showed on socket
  // disconnect/connect (the shared socket lifecycle now lives in useLiveRoom, so
  // react to its connection flag). Only warn after having connected once, so the
  // initial "connecting" state doesn't flash a "connection lost" toast.
  const wasSocketConnectedRef = useRef(false);
  useEffect(() => {
    if (isSocketConnected) {
      wasSocketConnectedRef.current = true;
      setNotification(null);
    } else if (wasSocketConnectedRef.current) {
      setNotification("Connection lost. Attempting to reconnect...");
    }
  }, [isSocketConnected]);

  // Student-specific: render the instructor's remote stream into #camera-video.
  // The shared lifecycle (Agora join, socket, chat, teardown) lives in useLiveRoom.
  useEffect(() => {
    if (!id) return;

    const handleGlobalError = (event: ErrorEvent) => {
      if (event.error?.name === "AbortError" || event.message?.includes("aborted")) {
        event.preventDefault();
        return false;
      }
    };
    window.addEventListener("error", handleGlobalError);

    if (!checkBrowserSupport()) {
      alert(
        "Your browser doesn't fully support video calling. Some features may not work properly. Please use Chrome, Firefox, Safari, or Edge for the best experience."
      );
    }

    const client = agoraServiceRef.current?.getClient();

    const onUserPublished = async (
      user: IAgoraRTCRemoteUser,
      mediaType: "audio" | "video"
    ) => {
      try {
        await agoraServiceRef.current?.getClient()?.subscribe(user, mediaType);
        remoteUserRef.current = user;
        if (mediaType === "video") {
          const videoElement = document.getElementById("camera-video") as HTMLVideoElement;
          if (videoElement) {
            videoElement.autoplay = true;
            videoElement.playsInline = true;
            videoElement.muted = false;
            videoElement.oncanplay = () => setIsConnecting(false);
            try {
              user.videoTrack?.play(videoElement);
              setIsConnecting(false);
            } catch {
              setTimeout(() => {
                try {
                  user.videoTrack?.play(videoElement);
                } catch {}
              }, 1000);
            }
          }
        }
        if (mediaType === "audio") {
          try {
            user.audioTrack?.play();
            user.audioTrack?.setVolume(audioVolume);
          } catch {}
        }
      } catch {
        setIsConnecting(false);
      }
    };
    const onUserUnpublished = (_user: any, mediaType: string) => {
      if (mediaType === "video") setIsConnecting(false);
    };
    const onUserLeft = () => setIsConnecting(false);

    client?.on("user-published", onUserPublished);
    client?.on("user-unpublished", onUserUnpublished);
    client?.on("user-left", onUserLeft);

    return () => {
      window.removeEventListener("error", handleGlobalError);
      client?.off("user-published", onUserPublished);
      client?.off("user-unpublished", onUserUnpublished);
      client?.off("user-left", onUserLeft);
      const videoElement = document.getElementById("camera-video") as HTMLVideoElement;
      if (videoElement?.srcObject) {
        const stream = videoElement.srcObject as MediaStream;
        stream.getTracks().forEach((track) => track.stop());
        videoElement.srcObject = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isSocketConnected]);

  // Message sending function
  const handleSendMessage = () => {
    if (!chatMessage.trim()) return;
    sendMessage(chatMessage);
    setChatMessage("");
  };

  // Chat message listener is now set up in initializeStudent function

  // Auto scroll chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Enhanced media control handlers
  const toggleMic = async () => {
    try {
      if (agoraServiceRef.current) {
        const newMicState = !isMicOn;
        
        // Control the student's own audio track using the service method
        await agoraServiceRef.current.setMicrophoneMuted(!newMicState);
        
        setIsMicOn(newMicState);
        console.log(`Student microphone state changed to: ${newMicState ? 'ON' : 'OFF'}`);
      }
    } catch (error) {
      console.error("Error toggling student microphone:", error);
      alert("Failed to control microphone. Please try again.");
    }
  };

  const handleVolumeChange = (volume: number) => {
    try {
      setAudioVolume(volume);
      // Students control the volume of instructor's audio
      if (remoteUserRef.current?.audioTrack) {
        remoteUserRef.current.audioTrack.setVolume(volume);
        console.log(`Instructor audio volume changed to: ${volume}`);
      }
    } catch (error) {
      console.error("Error changing instructor audio volume:", error);
    }
  };

  const toggleCamera = async () => {
    try {
      if (agoraServiceRef.current) {
        const newCameraState = !isCameraOn;
        
        console.log("=== STUDENT CAMERA TOGGLE ===");
        console.log("Current camera state:", isCameraOn);
        console.log("New camera state:", newCameraState);
        console.log("Agora client:", agoraServiceRef.current.getClient());
        
        if (newCameraState) {
          // Change role to host to allow publishing
          console.log("Setting client role to host...");
          await agoraServiceRef.current.getClient()?.setClientRole("host");
          console.log("Client role set to host");
          
          // Turn on camera - create and publish video track
          console.log("Creating camera video track...");
          const videoTrack = await AgoraRTC.createCameraVideoTrack();
          console.log("Video track created:", videoTrack);
          
          console.log("Publishing video track...");
          await agoraServiceRef.current.getClient()?.publish(videoTrack);
          console.log("Video track published");
          
          agoraServiceRef.current.localVideoTrack = videoTrack;
          
          // Play video in the student overlay
          const overlayElement = document.getElementById("student-video-overlay");
          console.log("Overlay element:", overlayElement);
          if (overlayElement) {
            videoTrack.play(overlayElement);
            console.log("Video playing in overlay");
            
          // Hide the camera off overlay
          const cameraOffOverlay = overlayElement.parentElement?.querySelector('.camera-off-overlay') as HTMLElement;
          if (cameraOffOverlay) {
            cameraOffOverlay.style.display = 'none';
            console.log("Camera off overlay hidden");
          }
          
          // Emit WebSocket event to notify instructor about camera status
          if (streamServiceRef.current?.socketInstance && id) {
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const eventData = {
              roomId: id,
              userId: user.id || user._id || user.userId,
              isCameraOff: false,
              role: 'user',
            };
            console.log("=== EMITTING CAMERA-ON EVENT ===");
            console.log("User object from localStorage:", user);
            console.log("Event data:", eventData);
            console.log("Socket connected:", streamServiceRef.current.socketInstance.connected);
            streamServiceRef.current.socketInstance.emit('camera-toggle', eventData);
            console.log("Emitted camera-on event to instructor");
          } else {
            console.error("Cannot emit camera event - missing socket or room ID");
            console.log("Socket instance:", !!streamServiceRef.current?.socketInstance);
            console.log("Room ID:", id);
          }
        } else {
            console.error("Could not find student-video-overlay element");
          }
        } else {
          // Turn off camera - unpublish and stop video track
          console.log("Turning off camera...");
          if (agoraServiceRef.current.localVideoTrack) {
            console.log("Unpublishing video track...");
            await agoraServiceRef.current.getClient()?.unpublish(agoraServiceRef.current.localVideoTrack);
            console.log("Stopping video track...");
            agoraServiceRef.current.localVideoTrack.stop();
            agoraServiceRef.current.localVideoTrack.close();
            agoraServiceRef.current.localVideoTrack = null;
            console.log("Video track stopped and cleaned up");
          }
          
          // Change role back to audience when camera is off
          console.log("Setting client role back to audience...");
          await agoraServiceRef.current.getClient()?.setClientRole("audience");
          console.log("Client role set to audience");
          
          // Show the camera off overlay again
          const overlayElement = document.getElementById("student-video-overlay");
          if (overlayElement) {
            const cameraOffOverlay = overlayElement.parentElement?.querySelector('.camera-off-overlay') as HTMLElement;
            if (cameraOffOverlay) {
              cameraOffOverlay.style.display = 'flex';
              console.log("Camera off overlay shown");
            }
          }
          
          // Emit WebSocket event to notify instructor about camera status
          if (streamServiceRef.current?.socketInstance && id) {
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const eventData = {
              roomId: id,
              userId: user.id || user._id || user.userId,
              isCameraOff: true,
              role: 'user',
            };
            console.log("=== EMITTING CAMERA-OFF EVENT ===");
            console.log("User object from localStorage:", user);
            console.log("Event data:", eventData);
            console.log("Socket connected:", streamServiceRef.current.socketInstance.connected);
            streamServiceRef.current.socketInstance.emit('camera-toggle', eventData);
            console.log("Emitted camera-off event to instructor");
          } else {
            console.error("Cannot emit camera event - missing socket or room ID");
            console.log("Socket instance:", !!streamServiceRef.current?.socketInstance);
            console.log("Room ID:", id);
          }
        }
        
        setIsCameraOn(newCameraState);
        console.log(`Camera ${newCameraState ? "turned on" : "turned off"}`);
        console.log("===============================");
      }
    } catch (error) {
      console.error("Error toggling camera:", error);
      alert("Failed to toggle camera. Please try again.");
    }
  };

  const handleHandRaise = async () => {
    try {
      if (streamServiceRef.current) {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const newHandRaisedState = !isHandRaised;

        // Emit hand raise/lower event using new format
        streamServiceRef.current.socketInstance.emit("hand-raise", {
          roomId: id,
          userId: user.id || user._id || user.userId,
          isRaised: newHandRaisedState,
          studentName: user.username || user.name || "Student",
        });

        setIsHandRaised(newHandRaisedState);

        // Show feedback notification
        const message = newHandRaisedState
          ? "Hand raised successfully!"
          : "Hand lowered";
        setNotification(message);
        setTimeout(() => setNotification(null), 3000);

        console.log(message);
      }
    } catch (error) {
      console.error("Error handling hand raise:", error);
    }
  };

  // Attach the student's participant / session socket listeners once the shared
  // socket is up. streamServiceRef.current is populated asynchronously by
  // useLiveRoom, so this effect must be gated on isSocketConnected (and have it
  // in the deps) — otherwise it returns early on the mount pass and, since `id`
  // never changes, never re-runs, leaving the student with no inbound listeners
  // (empty Participants panel, no navigation on end-session). Mirrors the
  // instructor's isSocketConnected-gated wiring effect.
  useEffect(() => {
    if (!id || !isSocketConnected || !streamServiceRef.current) return;

    const service = streamServiceRef.current;
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    // Join room and announce presence
    service.socketInstance.emit("participant-joined", {
      roomId: id,
      participant: {
        userId: user.id || user._id || user.userId,
        name: user.username || user.name || "Student",
        role: "user",
      },
    });

    // Listen for new participants
    service.socketInstance.on(
      "participant-joined",
      (data: { participant: Participant; timestamp: string }) => {
        console.log(`${data.participant.name} joined at ${data.timestamp}`);
        setParticipants((prev) => {
          if (prev.some((p) => p.userId === data.participant.userId)) {
            return prev;
          }
          return [...prev, data.participant];
        });
      }
    );

    // Listen for participants leaving
    service.socketInstance.on(
      "participant-left",
      (data: { participant: Participant; timestamp: string }) => {
        console.log(`${data.participant.name} left at ${data.timestamp}`);
        setParticipants((prev) =>
          prev.filter((p) => p.userId !== data.participant.userId)
        );
      }
    );

    // Request initial participants list
    service.socketInstance.emit("get-participants", { roomId: id });

    // Handle get-participants response
    service.socketInstance.on(
      "get-participants",
      (response: {
        success: boolean;
        participants?: Participant[];
        error?: string;
      }) => {
        if (response.success && response.participants) {
          console.log("Current participants:", response.participants);
          setParticipants(response.participants);
        } else {
          console.error("Failed to get participants:", response.error);
        }
      }
    );

    // Listen for participants list updates
    service.socketInstance.on(
      "participants-updated",
      (data: { participants: Participant[]; timestamp: string }) => {
        console.log("Participants list updated at", data);
        console.log(
          `Participants list updated at ${data.timestamp}:`,
          data.participants
        );
        setParticipants(data.participants);
      }
    );

    // Listen for session ending from instructor. The instructor emits
    // "end-session"; the backend re-broadcasts it to the room as "session-ended".
    service.socketInstance.on("session-ended", async () => {
      console.log("Session ended by instructor, cleaning up...");

      // Stop video element streams
      const videoElement = document.getElementById(
        "camera-video"
      ) as HTMLVideoElement;
      if (videoElement?.srcObject) {
        const stream = videoElement.srcObject as MediaStream;
        stream.getTracks().forEach((track) => track.stop());
        videoElement.srcObject = null;
      }

      // Reset states
      setIsCameraOn(false);
      setIsMicOn(false);

      // Navigate back to live classes after a short delay
      setTimeout(() => {
        navigate("/live-classes");
      }, 2000);
    });

    // Listen for instructor control actions
    service.socketInstance.on("instructor-control-action", (data: any) => {
      console.log("Instructor control action received:", data);
      
      if (data.action === "mute-all" || data.action === "mute-student") {
        // Handle instructor muting students
        if (data.action === "mute-student" && data.targetUserId) {
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          const currentUserId = user.id || user._id || user.userId;
          
          if (currentUserId === data.targetUserId) {
            setIsMicOn(false);
            setNotification("Instructor has muted your microphone");
            setTimeout(() => setNotification(null), 5000);
          }
        } else if (data.action === "mute-all") {
          setIsMicOn(false);
          setNotification("Instructor has muted all students");
          setTimeout(() => setNotification(null), 5000);
        }
      }
    });

    // Listen for mic status changes
    service.socketInstance.on("mic-status-changed", (data: any) => {
      console.log("Mic status changed:", data);
      
      // Show notification to student when instructor changes mic status
      if (data.role === "admin" || data.role === "instructor") {
        const status = data.isMuted ? "muted" : "unmuted";
        setNotification(`Instructor ${status} their microphone`);
        setTimeout(() => setNotification(null), 3000);
      }
    });

    // Listen for camera status changes
    service.socketInstance.on("camera-status-changed", (data: any) => {
      console.log("Camera status changed:", data);
      
      // Show notification to student when instructor changes camera status
      if (data.role === "admin" || data.role === "instructor") {
        const status = data.isCameraOff ? "turned off" : "turned on";
        setNotification(`Instructor ${status} their camera`);
        setTimeout(() => setNotification(null), 3000);
      }
    });

    // Listen for screen share status changes
    service.socketInstance.on("screen-share-status-changed", (data: any) => {
      console.log("=== STUDENT RECEIVED SCREEN-SHARE-STATUS-CHANGED ===");
      console.log("Screen share status changed:", data);
      console.log("Data role:", data.role);
      console.log("Data isScreenSharing:", data.isScreenSharing);
      console.log("Data instructorId:", data.instructorId);
      
      // Show notification to student when instructor changes screen share status
      if (data.role === "admin" || data.role === "instructor") {
        const status = data.isScreenSharing ? "started" : "stopped";
        setNotification(`Instructor ${status} screen sharing`);
        setTimeout(() => setNotification(null), 3000);
      }
    });

    // Listen for instructor lowering individual hand
    service.socketInstance.on("hand-lowered-by-instructor", (data: any) => {
      console.log("Hand lowered by instructor:", data);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const currentUserId = user.id || user._id || user.userId;
      
      if (data.targetUserId === currentUserId) {
        setIsHandRaised(false);
        setNotification("Instructor has lowered your hand");
        setTimeout(() => setNotification(null), 3000);
        console.log("Hand lowered by instructor");
      }
    });

    // Listen for instructor lowering all hands
    service.socketInstance.on("all-hands-lowered-by-instructor", (data: any) => {
      console.log("All hands lowered by instructor:", data);
      setIsHandRaised(false);
      setNotification("Instructor has lowered all hands");
      setTimeout(() => setNotification(null), 3000);
      console.log("All hands lowered by instructor");
    });

    // Listen for whiteboard toggle
    service.socketInstance.on("whiteboard-toggle", (data: any) => {
      console.log("Whiteboard toggle received:", data);
      setIsWhiteboardActive(data.isActive);
      if (data.isActive) {
        setNotification("📝 Instructor has activated the whiteboard!");
      } else {
        setNotification("📝 Instructor has hidden the whiteboard.");
      }
      setTimeout(() => setNotification(null), 3000);
    });

    // Whiteboard drawing events are no longer needed since students see whiteboard via screen sharing

    // Cleanup listeners on unmount
    return () => {
      service.socketInstance.off("participant-joined");
      service.socketInstance.off("participant-left");
      service.socketInstance.off("get-participants");
      service.socketInstance.off("participants-updated");
      service.socketInstance.off("session-ended");
      service.socketInstance.off("instructor-control-action");
      service.socketInstance.off("mic-status-changed");
      service.socketInstance.off("camera-status-changed");
      service.socketInstance.off("screen-share-status-changed");
      service.socketInstance.off("hand-lowered-by-instructor");
      service.socketInstance.off("all-hands-lowered-by-instructor");
      service.socketInstance.off("whiteboard-toggle");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isSocketConnected]);

  const handleLeaveClass = async () => {
    if (!window.confirm("Are you sure you want to leave the class?")) {
      return;
    }

    try {
      // Stop video element streams
      const videoElement = document.getElementById(
        "camera-video"
      ) as HTMLVideoElement;
      if (videoElement?.srcObject) {
        const stream = videoElement.srcObject as MediaStream;
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        videoElement.srcObject = null;
      }

      if (streamServiceRef.current) {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        // Emit participant-left before leaving
        streamServiceRef.current.socketInstance.emit("participant-left", {
          roomId: id,
          participant: {
            userId: user.id || user._id || user.userId,
            name: user.username || user.name || "Student",
            role: "user",
          },
        });

        streamServiceRef.current.stopStreaming();
      }

      // Reset camera and mic states
      setIsCameraOn(false);
      setIsMicOn(false);

      // Show leave message
      alert("You have left the classroom");

      navigate("/live-classes");
    } catch (error) {
      console.error("Error leaving class:", error);
      // Navigate anyway to prevent students from getting stuck
      navigate("/live-classes");
    }
  };

  return (
    <div className="h-screen bg-gray-100">
      {/* Notification Banner */}
      {notification && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm">{notification}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setNotification(null)}
                className="text-yellow-500 hover:text-yellow-700"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top Navigation */}
      <div className="bg-white shadow-sm px-6 py-3 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h1 className="text-xl font-semibold">{sessionTitle}</h1>
          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
            Live
          </span>
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-600">
            {participants.length} Participants
          </span>
          <button
            onClick={() => setShowLeaveConfirm(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Leave Class
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-64px)]">
        {/* Left Panel - Video/Screen Share */}
        <div className="flex-1 p-4">
          <div className="bg-white rounded-lg shadow-sm h-full flex flex-col">
            {/* Video Container */}
            <div className="flex-1 bg-gray-900 rounded-t-lg relative overflow-hidden">
              {/* Main Instructor Video */}
              <video
                id="camera-video"
                autoPlay
                playsInline
                className="w-full h-full object-contain"
                style={{ minHeight: "400px" }}
              />

              {/* Loading State */}
              {isConnecting && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-white text-lg">
                      Connecting to instructor's stream...
                    </p>
                    <p className="text-gray-300 text-sm mt-2">
                      Please wait while we establish the connection
                    </p>
                  </div>
                </div>
              )}

              {/* Camera Off Indicator */}
              {!isConnecting && !remoteUserRef.current?.videoTrack && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                  <div className="text-center">
                    <i className="fas fa-video-slash text-gray-400 text-6xl mb-4"></i>
                    <p className="text-gray-400 text-lg">
                      Instructor's camera is off
                    </p>
                    <p className="text-gray-500 text-sm mt-2">
                      Waiting for instructor to turn on their camera
                    </p>
                  </div>
                </div>
              )}

              {/* Whiteboard will be shown via screen sharing - no overlay needed */}

              {/* Status Indicators */}
              <div className="absolute top-4 left-4 flex flex-col gap-2 z-30">
                {!isSocketConnected ? (
                  <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm flex items-center">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    Disconnected
                  </div>
                ) : (
                  <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm flex items-center">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse mr-2"></div>
                    Connected
                  </div>
                )}

                {/* Audio Muted Indicator */}
                {!isMicOn && (
                  <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm flex items-center">
                    <i className="fas fa-volume-mute mr-2"></i>
                    Audio Muted
                  </div>
                )}

                {/* Hand Raised Indicator */}
                {isHandRaised && (
                  <div className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm flex items-center">
                    <i className="fas fa-hand-paper mr-2"></i>
                    Hand Raised
                  </div>
                )}

                {/* Whiteboard Active Indicator */}
                {isWhiteboardActive && (
                  <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm flex items-center">
                    <i className="fas fa-chalkboard mr-2"></i>
                    Whiteboard Active
                  </div>
                )}
              </div>

              {/* Network Quality Indicators removed */}

              {/* Student Video Overlay (Bottom Right) */}
              <div className="absolute bottom-4 right-4 w-48 h-36 bg-black rounded-lg border-2 border-white/20 overflow-hidden">
                <div
                  id="student-video-overlay"
                  className="w-full h-full flex items-center justify-center bg-gray-800"
                />
                
                {/* Camera Off Indicator */}
                {!isCameraOn && (
                  <div className="camera-off-overlay absolute inset-0 flex items-center justify-center bg-gray-800 z-10">
                    <div className="text-center text-white">
                      <i className="fas fa-user text-xl mb-1"></i>
                      <p className="text-xs">Your View</p>
                      <p className="text-xs text-gray-400">(Camera Off)</p>
                    </div>
                  </div>
                )}
                
                {/* Student Label */}
                <div className="absolute bottom-1 left-1 bg-black/70 text-white px-1 py-0.5 rounded text-xs">
                  You
                </div>
              </div>

              {/* Student Controls */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 flex justify-center items-end space-x-4">
                {/* Audio Control with Volume */}
                <div className="relative">
                  {/* Volume Slider */}
                  {showVolumeControl && (
                    <div className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-black/75 p-2 rounded-lg">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={audioVolume}
                        onChange={(e) =>
                          handleVolumeChange(Number(e.target.value))
                        }
                        className="w-20 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                        style={{
                          background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${audioVolume}%, #d1d5db ${audioVolume}%, #d1d5db 100%)`,
                        }}
                      />
                      <div className="text-white text-xs text-center mt-1">
                        {audioVolume}%
                      </div>
                    </div>
                  )}

                  <button
                    onClick={toggleMic}
                    onMouseEnter={() => setShowVolumeControl(true)}
                    onMouseLeave={() => setShowVolumeControl(false)}
                    className={`p-3 rounded-full relative group transition-all duration-200 ${
                      isMicOn
                        ? "bg-white/10 hover:bg-white/20"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                    title={isMicOn ? "Mute audio" : "Unmute audio"}
                  >
                    <i
                      className={`fas fa-volume-${
                        isMicOn ? "up" : "mute"
                      } text-white`}
                    />
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/75 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                      {isMicOn ? "Mute Audio" : "Unmute Audio"}
                    </span>
                  </button>
                </div>

                <button
                  onClick={toggleCamera}
                  className={`p-3 rounded-full relative group transition-all duration-200 ${
                    isCameraOn
                      ? "bg-white/10 hover:bg-white/20"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                  title={isCameraOn ? "Turn off camera" : "Turn on camera"}
                >
                  <i
                    className={`fas fa-video${
                      isCameraOn ? "" : "-slash"
                    } text-white`}
                  />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/75 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                    {isCameraOn ? "Turn Off Camera" : "Turn On Camera"}
                  </span>
                </button>

                <button
                  onClick={handleHandRaise}
                  className={`p-3 rounded-full relative group transition-all duration-200 ${
                    isHandRaised
                      ? "bg-yellow-500 hover:bg-yellow-600"
                      : "bg-white/10 hover:bg-white/20"
                  }`}
                  title={isHandRaised ? "Lower hand" : "Raise hand"}
                >
                  <i
                    className={`fas fa-hand-paper ${
                      isHandRaised ? "text-black" : "text-white"
                    }`}
                  />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/75 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                    {isHandRaised ? "Lower Hand" : "Raise Hand"}
                  </span>
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* Right Panel - Chat & Participants */}
        <div className="w-80 bg-white border-l">
          <div className="h-full flex flex-col">
            {/* Tabs */}
            <div className="flex border-b">
              <button
                onClick={() => setShowParticipants(false)}
                className={`flex-1 px-4 py-3 text-sm font-medium ${
                  !showParticipants
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Chat
              </button>
              <button
                onClick={() => setShowParticipants(true)}
                className={`flex-1 px-4 py-3 text-sm font-medium ${
                  showParticipants
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Participants
              </button>
            </div>

            {/* Content */}
            {!showParticipants ? (
              <>
                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-4">
                  {messages.length === 0 ? (
                    <div className="text-center text-gray-500 mt-8">
                      <i className="fas fa-comments text-4xl mb-4"></i>
                      <p>No messages yet.</p>
                      <p className="text-sm">Start the conversation!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <img
                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                              msg?.sender?.name || "Anonymous"
                            )}&background=3b82f6&color=ffffff`}
                            alt={msg?.sender?.name || "Anonymous"}
                            className="w-8 h-8 rounded-full flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-medium truncate">
                                {msg?.sender?.name || "Anonymous"}
                              </p>
                              {msg?.sender?.role === "admin" && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                  Instructor
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 mt-1 break-words">
                              {msg?.message || ""}
                            </p>
                            <span className="text-xs text-gray-400">
                              {msg?.timestamp
                                ? new Date(msg.timestamp).toLocaleTimeString()
                                : ""}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                {/* Chat Input */}
                <div className="p-4 border-t bg-gray-50">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && !e.shiftKey && handleSendMessage()
                      }
                      placeholder="Type a message..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      maxLength={500}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!chatMessage.trim() || !isSocketConnected}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center"
                      title={
                        !isSocketConnected
                          ? "Disconnected - cannot send messages"
                          : "Send message"
                      }
                    >
                      <i className="fas fa-paper-plane mr-1"></i>
                      Send
                    </button>
                  </div>
                  <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                    <span>Press Enter to send</span>
                    <span>{chatMessage.length}/500</span>
                  </div>
                </div>
              </>
            ) : (
              /* Participants List */
              <div className="flex-1 overflow-y-auto p-4">
                {participants.length === 0 ? (
                  <div className="text-center text-gray-500 mt-8">
                    <i className="fas fa-users text-4xl mb-4"></i>
                    <p>No participants yet.</p>
                    <p className="text-sm">Waiting for others to join...</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {participants.map((participant, index) => (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          participant.handRaised
                            ? "bg-yellow-50 border border-yellow-200"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <img
                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                              participant.name
                            )}&background=3b82f6&color=ffffff`}
                            alt={participant.name}
                            className="w-8 h-8 rounded-full flex-shrink-0"
                          />
                          <div>
                            <span className="font-medium text-sm">
                              {participant.name}
                            </span>
                            {participant.role === "admin" && (
                              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                Instructor
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {participant.handRaised && (
                            <div className="flex items-center text-yellow-600">
                              <i className="fas fa-hand-paper mr-1" />
                              <span className="text-xs">Hand raised</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Connection Status */}
      {!isSocketConnected && (
        <div className="fixed bottom-4 left-4 bg-red-100 text-red-700 px-4 py-2 rounded-md shadow-lg">
          <div className="flex items-center">
            <i className="fas fa-wifi text-red-600 mr-2"></i>
            Disconnected from server. Trying to reconnect...
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className="fixed top-4 right-4 bg-green-100 text-green-700 px-4 py-2 rounded-md shadow-lg z-50 animate-fade-in">
          <div className="flex items-center">
            <i className="fas fa-check-circle mr-2"></i>
            {notification}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={handleLeaveClass}
        title="Leave Class"
        message="Are you sure you want to leave the class? This action cannot be undone."
      />
    </div>
  );
};

export default LiveClassroom;
